/**
 * Centralized Access Decision Engine
 * 
 * Single source of truth for all UI gating decisions.
 * Returns structured results with reason codes, messages, and CTA actions.
 */

import {
  FeatureFlag,
  SubscriptionTier,
  SubscriptionStatus,
  isFeatureEnabledForTier,
  isRoleAllowedForTier,
  TIER_CONFIG,
  ROUTE_FEATURE_MAP,
  getTierDisplayConfig,
  getMinimumTierForRole,
  getMinimumTierForFeature,
} from '@/lib/subscriptionTiers';

// ─── Feature Benefit Copy ────────────────────────────────────────
// One-line value hooks surfaced in GatedNavItem tooltips.
// Goal: answer "what would I gain?" not just "what does it cost?".

export const FEATURE_BENEFIT_COPY: Partial<Record<FeatureFlag, string>> = {
  // Foundation
  fee_collection:           'Automate membership fee reminders and track who\'s paid.',
  opposition_analysis:      'Build detailed opponent profiles to walk into every game with a tactical edge.',
  drill_library:            'Access a library of structured drills to build varied, purposeful sessions.',
  compliance_tracker:       'Stay on top of certifications, safeguarding, and regulatory requirements.',
  basic_wellness_tracking:  'Log basic wellness check-ins so you can spot trends before they become problems.',
  csv_uploads:              'Upload GPS, wellness, and load data from spreadsheets to keep everything in one place.',
  org_risk_insights:        'Surface governance, compliance, and operational risks across your entire club.',
  // Foundation
  gps_analytics:            'See exactly how hard each player worked — and who\'s at risk of overload.',
  wellness_tracking:        'Track HRV, sleep, and soreness to catch burnout before it happens.',
  workload_analytics:       'Visualise acute and chronic workload trends across your entire squad.',
  video_analysis:           'Upload and annotate match footage to turn film sessions into decisions.',
  player_recruitment:       'Build a shortlist, track prospects, and compare scouting targets.',
  guardrails_management:    'Set automated load and wellness guardrails so nothing slips through the gaps.',
  venues:                   'Manage your club\'s pitches, facilities, and venue availability in one place.',
  historical_import:        'Import historical seasons of data to unlock long-term trend analysis.',
  match_congestion_management: 'Quantify fixture congestion and rotate your squad before fatigue causes injuries.',
  session_planner:          'Build load-optimised training weeks so your squad peaks on matchday.',
  performance_lab:          'Run side-by-side load comparisons and simulate training scenarios before committing.',
  multi_season_trends:      'Compare performance data across multiple seasons to track player development.',
  player_signal_score:      'A single composite score that tells you each player\'s current readiness at a glance.',
  weekly_ai_summary:        'AI-generated weekly coaching summary with predictive insights and recommendations.',
  rules_based_digest:       'A weekly coach briefing with key insights — threshold-based, no AI required.',
  // Performance Core — Medical & Governance
  travel_jet_lag:           'Model circadian disruption from travel and protect performance on away trips.',
  return_to_play:           'Structured, stage-gated rehab protocols so players return fit — not just available.',
  rtp_workflows:            'Configurable return-to-play workflows with stage gates and medical sign-off.',
  asymmetry_detection:      'Flag bilateral imbalances from fitness testing to reduce re-injury risk.',
  governance_audit_log:     'A tamper-proof log of every clinical decision for safeguarding and compliance.',
  // Performance Core (AI-powered)
  readiness_forecasting:    'AI-predicted readiness scores for every player before matchday.',
  ai_insights_hub:          'Instant briefings synthesised from GPS, wellness, and load data — ready before every session.',
  squad_gap_analysis:       'Identify positional weaknesses and coverage gaps before they cost you a result.',
  scenario_planner:         'Model squad rotation, injuries, and fixture lists to plan the season ahead.',
  compare_players:          'Benchmark any two players side-by-side across load, wellness, and performance metrics.',
  advanced_forecasting:     'Long-range AI forecasting that predicts injury risk weeks in advance.',
  ai_rtp_advisor:           'AI-assisted return-to-play recommendations based on rehab data and benchmarks.',
  ai_opposition_brief:      'AI-generated tactical briefs on upcoming opponents, ready before every match.',
};

// ─── Reason Codes ────────────────────────────────────────────────

export type DisabledReasonCode =
  | 'tier_required'
  | 'role_required'
  | 'trial_expired'
  | 'subscription_past_due'
  | 'subscription_canceled'
  | 'admin_only'
  | 'org_required'
  | 'seat_limit';

export type CtaAction =
  | 'open_billing'
  | 'request_admin'
  | 'open_payment_portal'
  | 'switch_org'
  | 'none';

export interface AccessDecision {
  allowed: boolean;
  disabledReasonCode?: DisabledReasonCode;
  message: string;
  ctaLabel: string;
  ctaAction: CtaAction;
  requiredTier?: SubscriptionTier;
  requiredRole?: string;
}

const ALLOWED: AccessDecision = {
  allowed: true,
  message: '',
  ctaLabel: '',
  ctaAction: 'none',
};

// ─── Centralized Reason-Code Copy Map ────────────────────────────
// Single source of truth for all denial messages.
// Keys match i18n translation keys for future localization.

export interface DenialCopyConfig {
  titleKey: string;
  messageKey: string;
  ctaLabelKey: string;
  ctaAction: CtaAction;
}

export const ACCESS_DENIAL_CONFIG: Record<DisabledReasonCode, DenialCopyConfig> = {
  tier_required: {
    titleKey: 'access_denied_tier_title',
    messageKey: 'access_denied_tier_message',
    ctaLabelKey: 'access_denied_tier_cta',
    ctaAction: 'open_billing',
  },
  role_required: {
    titleKey: 'access_denied_role_title',
    messageKey: 'access_denied_role_message',
    ctaLabelKey: 'access_denied_role_cta',
    ctaAction: 'open_billing',
  },
  trial_expired: {
    titleKey: 'access_denied_trial_expired_title',
    messageKey: 'access_denied_trial_expired_message',
    ctaLabelKey: 'access_denied_trial_expired_cta',
    ctaAction: 'open_billing',
  },
  subscription_past_due: {
    titleKey: 'access_denied_past_due_title',
    messageKey: 'access_denied_past_due_message',
    ctaLabelKey: 'access_denied_past_due_cta',
    ctaAction: 'open_payment_portal',
  },
  subscription_canceled: {
    titleKey: 'access_denied_canceled_title',
    messageKey: 'access_denied_canceled_message',
    ctaLabelKey: 'access_denied_canceled_cta',
    ctaAction: 'open_billing',
  },
  admin_only: {
    titleKey: 'access_denied_admin_title',
    messageKey: 'access_denied_admin_message',
    ctaLabelKey: 'access_denied_admin_cta',
    ctaAction: 'request_admin',
  },
  org_required: {
    titleKey: 'access_denied_org_title',
    messageKey: 'access_denied_org_message',
    ctaLabelKey: 'access_denied_org_cta',
    ctaAction: 'switch_org',
  },
  seat_limit: {
    titleKey: 'access_denied_seat_limit_title',
    messageKey: 'access_denied_seat_limit_message',
    ctaLabelKey: 'access_denied_seat_limit_cta',
    ctaAction: 'open_billing',
  },
};

// ─── Context for Decision ────────────────────────────────────────

export interface AccessContext {
  effectiveTier: SubscriptionTier;
  subscriptionStatus: SubscriptionStatus | string;
  hasAccess: boolean;
  isPaid: boolean;
  isBetaOrg: boolean;
  userRole?: string;
  organizationId?: string | null;
  organizationName?: string;
  userEmail?: string;
}

// ─── Tier Requirement Detection ──────────────────────────────────

/** Dynamically infer the minimum required tier for a feature flag */
function inferRequiredTier(feature: FeatureFlag): SubscriptionTier {
  return getMinimumTierForFeature(feature);
}

// ─── Build denial from config ────────────────────────────────────

function buildDenial(
  code: DisabledReasonCode,
  overrides?: { message?: string; ctaLabel?: string; requiredTier?: SubscriptionTier; requiredRole?: string },
): AccessDecision {
  const cfg = ACCESS_DENIAL_CONFIG[code];
  return {
    allowed: false,
    disabledReasonCode: code,
    message: overrides?.message ?? cfg.messageKey,
    ctaLabel: overrides?.ctaLabel ?? cfg.ctaLabelKey,
    ctaAction: cfg.ctaAction,
    requiredTier: overrides?.requiredTier,
    requiredRole: overrides?.requiredRole,
  };
}

// ─── Main Decision Function ──────────────────────────────────────

export function getAccessDecision(
  ctx: AccessContext,
  options: {
    feature?: FeatureFlag;
    requiredRole?: string;
    adminOnly?: boolean;
    routePath?: string;
  } = {},
): AccessDecision {
  const { feature, requiredRole, adminOnly, routePath } = options;

  // 1. No org → must join/create
  if (!ctx.organizationId) {
    return buildDenial('org_required', {
      message: 'You need to be part of an organization to access this feature.',
      ctaLabel: 'Switch organization',
    });
  }

  // 2. Beta orgs bypass all checks
  if (ctx.isBetaOrg) return ALLOWED;

  // 3. Subscription status checks
  // Expired = fully locked. No fallback tier.
  if (ctx.subscriptionStatus === 'expired') {
    return buildDenial('trial_expired', {
      message: 'Your subscription is inactive. Resubscribe to regain access.',
      ctaLabel: 'Upgrade plan',
    });
  }

  if (ctx.subscriptionStatus === 'past_due') {
    return buildDenial('subscription_past_due', {
      message: 'Your payment is past due. Update your payment method to restore access.',
      ctaLabel: 'Update payment',
    });
  }

  if (ctx.subscriptionStatus === 'canceled') {
    return buildDenial('subscription_canceled', {
      message: 'Your subscription has been canceled. Resubscribe to regain access.',
      ctaLabel: 'Upgrade plan',
    });
  }

  // 4. Admin-only check
  if (adminOnly && ctx.userRole !== 'org_admin' && ctx.userRole !== 'super_admin') {
    return buildDenial('admin_only', {
      message: 'Only organization admins can perform this action.',
      ctaLabel: 'Ask org admin',
    });
  }

  // 5. Role check (tier-dependent roles like medical)
  if (requiredRole && !isRoleAllowedForTier(ctx.effectiveTier, requiredRole)) {
    const requiredTier = getMinimumTierForRole(requiredRole);
    return buildDenial('role_required', {
      message: `The ${requiredRole} role requires the ${getTierDisplayConfig(requiredTier).name} plan.`,
      ctaLabel: 'Upgrade plan',
      requiredTier,
      requiredRole,
    });
  }

  // 6. Feature check — resolve from route if not explicit
  const resolvedFeature = feature || (routePath ? ROUTE_FEATURE_MAP[routePath] : undefined);


  if (resolvedFeature && !isFeatureEnabledForTier(ctx.effectiveTier, resolvedFeature)) {
    const requiredTier = inferRequiredTier(resolvedFeature);
    const tierConfig = TIER_CONFIG[requiredTier];
    const benefit = FEATURE_BENEFIT_COPY[resolvedFeature];
    const requiresLine = `Requires ${tierConfig.name} (€${tierConfig.price}/mo).`;
    return buildDenial('tier_required', {
      message: benefit ? `${benefit} ${requiresLine}` : requiresLine,
      ctaLabel: 'Upgrade plan',
      requiredTier,
    });
  }

  return ALLOWED;
}

// ─── Request Admin Message Helper ────────────────────────────────

export function getAdminRequestMessage(
  ctx?: { organizationName?: string; userEmail?: string },
  featureName?: string,
  requiredTier?: SubscriptionTier,
): string {
  const tierName = requiredTier ? getTierDisplayConfig(requiredTier).name : 'a higher plan';
  const featureStr = featureName ? ` to enable "${featureName}"` : '';
  const orgStr = ctx?.organizationName ? ` for "${ctx.organizationName}"` : ' for our organization';
  const userStr = ctx?.userEmail ? `\n\nRequested by: ${ctx.userEmail}` : '';
  const billingPath = '\nUpgrade here: /app/settings/billing';
  return `Please upgrade AlphaSignal to ${tierName}${featureStr}${orgStr}.${userStr}${billingPath}`;
}

// ─── Edge Function Denial Normalizer ─────────────────────────────
// Converts structured {allowed: false, reason, required_tier} from edge
// functions into the same AccessDecision shape. Prevents generic error toasts.

export interface EdgeFunctionDenial {
  allowed: false;
  reason?: string;
  required_tier?: string;
  message?: string;
}

export function isEdgeFunctionDenial(data: unknown): data is EdgeFunctionDenial {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as any).allowed === false &&
    typeof (data as any).reason === 'string'
  );
}

export function normalizeEdgeDenial(data: EdgeFunctionDenial): AccessDecision {
  const reasonMap: Record<string, DisabledReasonCode> = {
    tier_required: 'tier_required',
    role_required: 'role_required',
    trial_expired: 'trial_expired',
    subscription_past_due: 'subscription_past_due',
    subscription_canceled: 'subscription_canceled',
    admin_only: 'admin_only',
    org_required: 'org_required',
    seat_limit: 'seat_limit',
  };
  const code = reasonMap[data.reason ?? ''] ?? 'tier_required';
  const requiredTier = data.required_tier as SubscriptionTier | undefined;
  const tierConfig = requiredTier ? TIER_CONFIG[requiredTier] : undefined;

  return buildDenial(code, {
    message: data.message ?? (tierConfig
      ? `This action requires the ${tierConfig.name} plan.`
      : 'Access denied. Your current plan does not include this feature.'),
    ctaLabel: ACCESS_DENIAL_CONFIG[code].ctaAction === 'open_billing' ? 'Upgrade plan' : ACCESS_DENIAL_CONFIG[code].ctaLabelKey,
    requiredTier,
  });
}
